/**
 * @file: FieldErrorAnchorView.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useShakeEffect } from '@euler/functions/useShakeEffect';
import { ComponentRef, ReactNode, memo, useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import { FieldErrorAnchorKey } from './anchorKeys';
import { useFieldErrorFocus } from './context';
import { measureInWindowAsync } from './measureInWindowAsync';

const kShakeOffset = 8;

/**
 * makes the wrapped form row focusable by the field error focus controller:
 * it can be located on screen and shaken when its field fails validation.
 * the haptics are played by the submit handler, not by the shake itself.
 */
export const FieldErrorAnchorView = memo(
  ({
    anchorKey,
    children,
  }: {
    anchorKey: FieldErrorAnchorKey;
    children?: ReactNode;
  }) => {
    const focus = useFieldErrorFocus();
    const viewRef = useRef<ComponentRef<typeof Animated.View>>(null);
    const { shakeStyle, shake } = useShakeEffect(kShakeOffset, {
      haptics: false,
    });

    const registerAnchor = focus?.registerAnchor;

    useEffect(() => {
      if (registerAnchor == null) return;
      return registerAnchor(anchorKey, {
        measure: () => measureInWindowAsync(viewRef.current),
        shake,
      });
    }, [anchorKey, registerAnchor, shake]);

    return (
      <Animated.View ref={viewRef} style={[styles.anchor, shakeStyle]}>
        {children}
      </Animated.View>
    );
  },
);

const styles = StyleSheet.create({
  anchor: {
    // matches the table view item: never shrink inside a scroll view
    flexShrink: 0,
  },
});
